
import { PricingTierCard } from "@/components/pricing/PricingTierCard";
import { PricingTiers } from "@/config/pricing";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Prices = () => {
  const { handleSubscribe } = useSubscription();
  const navigate = useNavigate();

  return (
    <div className="container mx-auto py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-8 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back Home
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-lg text-muted-foreground">
            Choose the perfect plan for your coaching needs
          </p>
        </div>

        {/* Feature Comparison */}
        <div className="mb-16 bg-muted/50 p-6 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4 text-center">Feature Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-3 text-center">Free Tier</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Basic drill creation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  2D drill preview only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✗</span> 
                  No downloads or exports
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✗</span> 
                  Limited to 5 drills per month
                </li>
              </ul>
            </div>
            
            <div className="bg-card rounded-lg p-6 shadow-sm border border-primary">
              <h3 className="text-xl font-semibold mb-3 text-center">Pro</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Advanced drill creation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  3D drill simulations
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Advanced AI analytics
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Unlimited drills
                </li>
              </ul>
            </div>
            
            <div className="bg-card rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-3 text-center">Enterprise</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Team analytics dashboard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Coach collaboration platform
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Custom AI reports
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">✓</span> 
                  Dedicated account manager
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Standard Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {PricingTiers.slice(0, 3).map((tier) => (
            <PricingTierCard 
              key={tier.name}
              tier={tier}
              onSubscribe={handleSubscribe}
            />
          ))}
        </div>

        {/* B2B Solution */}
        <div className="bg-muted rounded-xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">B2B Solutions for Clubs & Academies</h2>
            <p className="text-lg max-w-3xl mx-auto">
              Customized team analytics dashboards and enterprise-level solutions 
              designed specifically for football clubs and academies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Team Analytics Dashboard</h3>
              <p className="text-muted-foreground mb-4">
                Comprehensive performance tracking for all your players with seasonal data reports.
              </p>
              <p className="font-medium">Starting at $500 per season</p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Price varies based on team size ($500-$2,000)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Includes premium onboarding and training
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Custom branding and white-labeling
                </li>
              </ul>
            </div>

            <div className="bg-card rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">API Integration</h3>
              <p className="text-muted-foreground mb-4">
                Data integration capabilities for third-party coaching applications and systems.
              </p>
              <p className="font-medium">Custom pricing</p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Full API access for your technical team
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Export data to your existing systems
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span> 
                  Technical implementation support
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => handleSubscribe(PricingTiers[3])}
              size="lg"
              className="bg-primary hover:bg-primary/90"
            >
              Request Custom Quote
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              Our team will contact you within 24 hours to discuss your specific needs
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prices;
